// Statuses that describe the user's own situation rather than a fault.
const EXPECTED_STATUSES = [401, 403, 422];

// merchy ThreadController::createThread answers the used-up weekly/daily deal limit with
// 406. Only there: a 406 anywhere else would be a content-negotiation fault.
const isDealLimit = (status, url) =>
  status === 406 && /^\/?threads\/type\/\d+$/.test(String(url ?? '').split('?')[0]);

// merchy BusinessCentralDocumentNotFound, word for word. Matched on the text because the
// same route answers 404 "File Not Found." for a vanished legacy file, which is a defect.
const ERP_NOT_READY_MESSAGE = 'This invoice is not yet available from the accounting system.';

/**
 * @description Whether a download failed only because the ERP has not issued the PDF yet.
 *
 * Expected until the ERP creates the batch's documents; the same download then succeeds.
 * @param status HTTP status of the failed response
 * @param url request url relative to the api root, e.g. "/invoices/12/download"
 * @param message the server's message
 * @returns {boolean}
 */
export const isInvoiceNotInErpYet = (status, url, message) =>
  status === 404
  && message === ERP_NOT_READY_MESSAGE
  && /^\/?invoices\/(\d+\/download|download\/[^/]+\/[^/]+)$/.test(String(url ?? '').split('?')[0]);

/**
 * @description Whether a failed response is an answer rather than a defect.
 *
 * A wrong password, an action the user's role does not allow, a deal quota that is
 * used up and a form that did not validate are all outcomes the system is supposed to produce. They are shown
 * to the user and not reported: Bugsnag is for what nobody expected, and these were
 * drowning it — the loudest group in the project was a permission denial.
 *
 * 404 stays reportable, because from here a resource that does not exist and an
 * endpoint that does not exist look the same. The one exception is an invoice the
 * ERP has not issued yet, which the server tells apart by its message.
 *
 * Lives apart from `./axios` for the same reason `./unauthorizedDecision` does —
 * importing that module registers the interceptors and pulls in the store and
 * router, and `responseErrorHandler` returns early under NODE_ENV=test, which
 * leaves this branch unreachable from a unit test. Pure, with no dependencies.
 * @param status HTTP status of the failed response
 * @param url request url relative to the api root, e.g. "/threads/type/1"
 * @param message the server's message
 * @returns {boolean}
 */
export const isExpectedOutcome = (status, url, message) =>
  EXPECTED_STATUSES.includes(status)
  || isDealLimit(status, url)
  || isInvoiceNotInErpYet(status, url, message);

export default isExpectedOutcome;
